/**
 * Estimate → New Job prefill helpers (APPROVED ESTIMATES §6c).
 * Pure module so the job-type derivation can be unit tested without
 * rendering the form.
 */

export interface EstimatePrefillLine {
    id: string;
    line_type: string;
    description: string | null;
}

export interface EstimatePrefill {
    id: string;
    display_number: string | null;
    customer_id: string;
    site_id: string | null;
    status: string;
    title: string | null;
    subtotal: string | null;
    approved_by_name?: string | null;
    approved_via_token_at?: string | null;
}

/** Parse an inches size out of free text ("2 1/2\"", "3/4\"", "1.5\"").
 *  Mirrors the server-side repairJobScope.parseSizeInches fraction
 *  handling so 3/4" never reads as 3.4". */
export function parseInches(text: string): number {
    let m = text.match(/(\d+)[\s-](\d+)\/(\d+)\s*(?:"|”|in\b|inch)/i);
    if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
    m = text.match(/(\d+)\/(\d+)\s*(?:"|”|in\b|inch)/i);
    if (m) return Number(m[1]) / Number(m[2]);
    m = text.match(/(\d+(?:\.\d+)?)\s*(?:"|”|in\b|inch)/i);
    return m ? parseFloat(m[1]) : 0;
}

/** Derive the job type from an approved estimate's lines: repair lines →
 *  Backflow Repair, replacement-only estimates → Backflow Replacement,
 *  with small/large picked from the biggest device size mentioned
 *  (≥ 2.5" = large). Returns the matching job_types.code, or null when
 *  the lines don't read as backflow repair work. */
export function deriveJobTypeCodeFromEstimateLines(lines: EstimatePrefillLine[]): string | null {
    const texts = lines.map((l) => (l.description ?? "").toLowerCase()).filter(Boolean);
    const repairish = texts.filter((t) => /repair|rebuild|replace|kit\b/.test(t));
    if (repairish.length === 0) return null;
    const replaceLines = repairish.filter((t) => /replace/.test(t));
    const isAllReplace = replaceLines.length > 0 && replaceLines.length === repairish.length;
    const maxInches = Math.max(0, ...texts.map(parseInches));
    const large = maxInches >= 2.5;
    if (isAllReplace) return large ? "backflow_replace_large" : "backflow_replace_small";
    return large ? "backflow_repair_large" : "backflow_repair_small";
}
